import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { firestore, storage } from '../database/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { toast } from 'react-toastify';

const SUBMISSIONS_COLLECTION = 'product_development';
const AUDIT_CHECKLISTS_COLLECTION = 'productDevelopmentAuditChecklists';
const AUDITS_COLLECTION = 'product_development_audits';

// Submission statuses
export const SUBMISSION_STATUS = {
  SUBMITTED: 'submitted',
  UNDER_REVIEW: 'under_review',
  CHANGES_REQUIRED: 'changes_required',
  APPROVED: 'approved',
};

// Categories
export const CATEGORIES = {
  SOFTWARE: 'software',
  NON_SOFTWARE: 'non-software', // Encompasses hardware, physical products (teas, soaps, etc.)
};

const generateFeedbackId = () =>
  `fb_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * Get all submissions for a user
 */
export const getUserSubmissions = async (userId, stage = null) => {
  try {
    const submissionsRef = collection(firestore, SUBMISSIONS_COLLECTION);
    // Filter by userId first (no index needed for single field)
    let q = query(submissionsRef, where('userId', '==', userId));

    const querySnapshot = await getDocs(q);
    let submissions = [];

    querySnapshot.forEach(doc => {
      const data = doc.data();
      submissions.push({
        id: doc.id,
        ...data,
      });
    });

    // Filter by stage if provided (client-side filtering to avoid index requirement)
    if (stage) {
      submissions = submissions.filter(sub => sub.stage === stage);
    }

    // Sort by createdAt in descending order (client-side sorting)
    submissions.sort((a, b) => {
      const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
      return bDate - aDate; // Descending order
    });

    return submissions;
  } catch (error) {
    throw error;
  }
};

/**
 * Get all submissions with enhanced filtering (category, status, pagination)
 * For staff roles (Admin, Moderator, BusinessAdvisor, Coach): Returns ALL submissions
 * For clients: Returns only their own submissions
 */
export const getAllSubmissions = async ({
  stage = null,
  userId = null,
  userEmail = null,
  category = null,
  status = null,
  searchTerm = '',
  sortBy = 'createdAt',
  sortOrder = 'desc',
  limit = null,
  startAfter = null,
  userRole = null, // Add userRole parameter for staff access
} = {}) => {
  try {
    const submissionsRef = collection(firestore, SUBMISSIONS_COLLECTION);
    let q = query(submissionsRef);

    // Staff roles can see all submissions - don't filter by userId
    const isStaff =
      userRole &&
      ['admin', 'moderator', 'businessadvisor', 'coach', 'superAdmin', 'superAdmins'].includes(
        userRole.toLowerCase()
      );

    // If not staff and userId is provided, filter by userId
    if (!isStaff && userId) {
      q = query(q, where('userId', '==', userId));
    }

    // Apply Firestore filters
    if (category) {
      q = query(q, where('category', '==', category));
    }
    if (status) {
      q = query(q, where('status', '==', status));
    }

    // Apply sorting
    if (sortBy === 'createdAt') {
      q = query(q, orderBy('createdAt', sortOrder));
    } else if (sortBy === 'status') {
      q = query(q, orderBy('status', sortOrder));
    }

    // Apply pagination
    if (startAfter) {
      q = query(q, startAfter(startAfter));
    }
    if (limit) {
      q = query(q, limit(limit));
    }

    const querySnapshot = await getDocs(q);
    let submissions = [];

    querySnapshot.forEach(docSnap => {
      const data = docSnap.data();
      submissions.push({
        id: docSnap.id,
        ...data,
      });
    });

    // Client-side filtering for complex queries
    submissions = submissions.filter(submission => {
      // If not staff, ensure user can only see their own submissions
      if (!isStaff) {
        if (userId && submission.userId !== userId) return false;
        if (userEmail && submission.userEmail?.toLowerCase() !== userEmail.toLowerCase())
          return false;
      }
      if (stage && submission.stage !== stage) return false;
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const searchableText =
          `${submission.title || ''} ${submission.description || ''} ${submission.category || ''} ${submission.status || ''} ${submission.userEmail || ''}`.toLowerCase();
        if (!searchableText.includes(searchLower)) return false;
      }
      return true;
    });

    // Additional client-side sorting if needed
    if (sortBy !== 'createdAt' && sortBy !== 'status') {
      submissions.sort((a, b) => {
        const aVal = a[sortBy] || '';
        const bVal = b[sortBy] || '';
        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });
    }

    return submissions;
  } catch (error) {
    toast.error('Error fetching submissions');
    throw error;
  }
};

/**
 * Get a single submission by ID
 */
export const getSubmissionById = async id => {
  try {
    const submissionRef = doc(firestore, SUBMISSIONS_COLLECTION, id);
    const submissionSnap = await getDoc(submissionRef);

    if (submissionSnap.exists()) {
      return {
        id: submissionSnap.id,
        ...submissionSnap.data(),
      };
    }
    return null;
  } catch (error) {
    throw error;
  }
};

/**
 * Generate automatic audit report for a submission
 */
const generateAutomaticAuditReport = (submission, submissionId) => {
  const category = submission.category || CATEGORIES.SOFTWARE;

  // Calculate completeness score based on filled fields
  const requiredFields = [
    'title',
    'description',
    category === CATEGORIES.SOFTWARE ? 'programmingLanguages' : 'hardwareType',
    category === CATEGORIES.SOFTWARE ? 'frameworks' : 'iotCategory',
    category === CATEGORIES.SOFTWARE ? 'technologies' : 'schematics',
    category === CATEGORIES.SOFTWARE ? 'architecture' : 'pcbDesign',
    category === CATEGORIES.SOFTWARE ? 'requirements' : 'microcontrollers',
    category === CATEGORIES.SOFTWARE ? 'cloudSetup' : 'sensors',
    category === CATEGORIES.SOFTWARE ? 'databases' : 'connectivity',
    category === CATEGORIES.SOFTWARE ? 'apis' : 'powerSystem',
    category === CATEGORIES.SOFTWARE ? 'uiux' : 'firmware',
    category === CATEGORIES.SOFTWARE ? 'security' : 'prototypeDetails',
    category === CATEGORIES.SOFTWARE ? 'testing' : 'targetIndustry',
    category === CATEGORIES.SOFTWARE ? 'industries' : null,
  ].filter(Boolean);

  let filledFields = 0;
  requiredFields.forEach(field => {
    const value = submission[field];
    if (value) {
      if (Array.isArray(value) && value.length > 0) {
        filledFields++;
      } else if (typeof value === 'string' && value.trim().length > 0) {
        filledFields++;
      }
    }
  });

  const completenessScore =
    requiredFields.length > 0 ? Math.round((filledFields / requiredFields.length) * 100) : 0;

  // Determine readiness level
  let readinessLevel = 'Critical';
  let readinessColor = 'red';
  if (completenessScore >= 90) {
    readinessLevel = 'Excellent';
    readinessColor = 'green';
  } else if (completenessScore >= 75) {
    readinessLevel = 'Good';
    readinessColor = 'blue';
  } else if (completenessScore >= 60) {
    readinessLevel = 'Fair';
    readinessColor = 'yellow';
  } else if (completenessScore >= 40) {
    readinessLevel = 'Needs Improvement';
    readinessColor = 'orange';
  }

  // Generate report summary
  const reportSummary = `
AUTOMATIC AUDIT REPORT - ${submission.title || 'Untitled Submission'}

Submission Details:
- Category: ${category === CATEGORIES.SOFTWARE ? 'Software' : 'Hardware'}
- Stage: ${submission.stageName || submission.stage || 'Development'}
- Submitted by: ${submission.userEmail || 'Unknown'}
- Submission Date: ${new Date().toLocaleDateString()}

Completeness Assessment:
- Completeness Score: ${completenessScore}%
- Readiness Level: ${readinessLevel}
- Fields Filled: ${filledFields} / ${requiredFields.length}

Technical Assessment:
${
  category === CATEGORIES.SOFTWARE
    ? `- Programming Languages: ${Array.isArray(submission.programmingLanguages) ? submission.programmingLanguages.join(', ') : 'Not specified'}
- Frameworks: ${Array.isArray(submission.frameworks) ? submission.frameworks.join(', ') : 'Not specified'}
- Technologies: ${Array.isArray(submission.technologies) ? submission.technologies.join(', ') : 'Not specified'}
- Target Industries: ${Array.isArray(submission.industries) ? submission.industries.join(', ') : 'Not specified'}`
    : `- Hardware Type: ${Array.isArray(submission.hardwareType) ? submission.hardwareType.join(', ') : 'Not specified'}
- IoT Category: ${Array.isArray(submission.iotCategory) ? submission.iotCategory.join(', ') : 'Not specified'}
- Target Industries: ${Array.isArray(submission.targetIndustry) ? submission.targetIndustry.join(', ') : 'Not specified'}`
}

Recommendations:
${
  completenessScore < 60
    ? `- Submission requires significant additional information before review can proceed.
- Please ensure all required fields are completed with detailed technical specifications.
- Consider adding more comprehensive documentation and technical details.`
    : completenessScore < 90
      ? `- Submission is mostly complete but could benefit from additional details.
- Review and enhance technical specifications where applicable.
- Ensure all optional fields are filled for comprehensive assessment.`
      : `- Submission is comprehensive and ready for detailed review.
- All required information has been provided.
- Proceed with technical audit and evaluation.`
}

Status: ${submission.status || SUBMISSION_STATUS.SUBMITTED}
Next Steps: Awaiting staff review and detailed audit.

---
This is an automatically generated audit report created upon submission.
  `.trim();

  return {
    submissionId,
    category,
    auditType: 'automatic',
    reportSummary,
    completenessScore,
    readinessLevel,
    readinessColor,
    filledFields,
    totalFields: requiredFields.length,
    checklistItems: [], // Can be populated later by staff
    comments: '',
    attachments: [],
    status: SUBMISSION_STATUS.UNDER_REVIEW,
    auditorId: 'system',
    auditorEmail: 'system@automatic',
    auditorName: 'Automatic Audit System',
    auditDate: serverTimestamp(),
  };
};

/**
 * Create a new submission with category and status
 * Automatically creates an audit report for staff review
 */
export const createSubmission = async (submissionData, userId, userEmail) => {
  try {
    const submissionsRef = collection(firestore, SUBMISSIONS_COLLECTION);
    const newSubmission = {
      ...submissionData,
      userId,
      userEmail,
      clientId: userId, // For compatibility
      category: submissionData.category || CATEGORIES.SOFTWARE,
      status: submissionData.status || SUBMISSION_STATUS.SUBMITTED,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(submissionsRef, newSubmission);
    const submissionId = docRef.id;

    // Automatically create audit report
    try {
      const auditData = generateAutomaticAuditReport(
        { ...newSubmission, id: submissionId },
        submissionId
      );

      // Update submission status to UNDER_REVIEW
      await updateDoc(docRef, {
        status: SUBMISSION_STATUS.UNDER_REVIEW,
      });

      // Create the audit
      const auditsRef = collection(firestore, AUDITS_COLLECTION);
      await addDoc(auditsRef, auditData);
    } catch (auditError) {
      toast.error('Error creating automatic audit report');
    }

    return {
      id: submissionId,
      ...newSubmission,
    };
  } catch (error) {
    throw error;
  }
};

/**
 * Update a submission
 */
export const updateSubmission = async (id, submissionData) => {
  try {
    const submissionRef = doc(firestore, SUBMISSIONS_COLLECTION, id);
    const updateData = {
      ...submissionData,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(submissionRef, updateData);
    return { id, ...updateData };
  } catch (error) {
    throw error;
  }
};

/**
 * Add feedback to a submission
 */
export const addSubmissionFeedback = async (submissionId, feedbackData) => {
  try {
    const submissionRef = doc(firestore, SUBMISSIONS_COLLECTION, submissionId);
    const submissionSnap = await getDoc(submissionRef);

    if (!submissionSnap.exists()) {
      throw new Error('Submission not found');
    }

    const existingData = submissionSnap.data();
    const existingFeedback = Array.isArray(existingData.feedback) ? existingData.feedback : [];

    const feedbackEntry = {
      id: feedbackData.id || generateFeedbackId(),
      comment: feedbackData.comment || '',
      status: feedbackData.status || 'reviewed',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      createdById: feedbackData.createdById || null,
      createdByEmail: feedbackData.createdByEmail || null,
      createdByRole: feedbackData.createdByRole || null,
      createdByName: feedbackData.createdByName || null,
      attachments: feedbackData.attachments || [],
    };

    const nextFeedback = [...existingFeedback, feedbackEntry];

    await updateDoc(submissionRef, {
      feedback: nextFeedback,
      updatedAt: serverTimestamp(),
    });

    return feedbackEntry;
  } catch (error) {
    toast.error('Error adding feedback');
    throw error;
  }
};

/**
 * Update feedback entry on a submission
 */
export const updateSubmissionFeedback = async (submissionId, feedbackId, updates) => {
  try {
    const submissionRef = doc(firestore, SUBMISSIONS_COLLECTION, submissionId);
    const submissionSnap = await getDoc(submissionRef);

    if (!submissionSnap.exists()) {
      throw new Error('Submission not found');
    }

    const existingData = submissionSnap.data();
    const existingFeedback = Array.isArray(existingData.feedback) ? existingData.feedback : [];

    const updatedFeedback = existingFeedback.map(entry => {
      if (entry.id !== feedbackId) return entry;
      return {
        ...entry,
        ...updates,
        updatedAt: Timestamp.now(),
      };
    });

    await updateDoc(submissionRef, {
      feedback: updatedFeedback,
      updatedAt: serverTimestamp(),
    });

    return updatedFeedback.find(entry => entry.id === feedbackId) || null;
  } catch (error) {
    toast.error('Error updating feedback');
    throw error;
  }
};

/**
 * Delete feedback entry from a submission
 */
export const deleteSubmissionFeedback = async (submissionId, feedbackId) => {
  try {
    const submissionRef = doc(firestore, SUBMISSIONS_COLLECTION, submissionId);
    const submissionSnap = await getDoc(submissionRef);

    if (!submissionSnap.exists()) {
      throw new Error('Submission not found');
    }

    const existingData = submissionSnap.data();
    const existingFeedback = Array.isArray(existingData.feedback) ? existingData.feedback : [];

    const filteredFeedback = existingFeedback.filter(entry => entry.id !== feedbackId);

    await updateDoc(submissionRef, {
      feedback: filteredFeedback,
      updatedAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    toast.error('Error deleting feedback');
    throw error;
  }
};

/**
 * Delete a submission
 */
export const deleteSubmission = async id => {
  try {
    const submissionRef = doc(firestore, SUBMISSIONS_COLLECTION, id);
    const submissionSnap = await getDoc(submissionRef);

    if (submissionSnap.exists()) {
      const data = submissionSnap.data();
      // Delete associated files
      if (data.attachments && Array.isArray(data.attachments)) {
        for (const attachment of data.attachments) {
          if (attachment.storagePath) {
            try {
              const fileRef = ref(storage, attachment.storagePath);
              await deleteObject(fileRef);
            } catch (err) {
              toast.error('Error deleting file');
            }
          }
        }
      }
    }

    await deleteDoc(submissionRef);
    return true;
  } catch (error) {
    toast.error('Error deleting submission');
    throw error;
  }
};

/**
 * Upload file attachment
 */
export const uploadAttachment = async (file, userId, submissionId) => {
  try {
    const filePath = `product-development/${userId}/${submissionId}/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, filePath);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    return {
      name: file.name,
      size: file.size,
      type: file.type,
      downloadURL,
      storagePath: filePath,
      uploadedAt: new Date().toISOString(),
    };
  } catch (error) {
    toast.error('Error uploading file');
    throw error;
  }
};

/**
 * Get audit checklists for a stage (for admins/coaches/moderators)
 */
export const getAuditChecklists = async (stage = null) => {
  try {
    const checklistsRef = collection(firestore, AUDIT_CHECKLISTS_COLLECTION);
    let q = query(checklistsRef);

    const querySnapshot = await getDocs(q);
    let checklists = [];

    querySnapshot.forEach(doc => {
      const data = doc.data();
      checklists.push({
        id: doc.id,
        ...data,
      });
    });

    // Filter by stage if provided (client-side filtering to avoid index requirement)
    if (stage) {
      checklists = checklists.filter(checklist => checklist.stage === stage);
    }

    // Sort by createdAt in descending order (client-side sorting)
    checklists.sort((a, b) => {
      const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
      return bDate - aDate; // Descending order
    });

    return checklists;
  } catch (error) {
    toast.error('Error fetching audit checklists');
    throw error;
  }
};

/**
 * Create audit checklist (for admins/coaches/moderators)
 */
export const createAuditChecklist = async (checklistData, userId, userEmail, userRole) => {
  try {
    const checklistsRef = collection(firestore, AUDIT_CHECKLISTS_COLLECTION);
    const newChecklist = {
      ...checklistData,
      createdBy: userId,
      createdByEmail: userEmail,
      createdByRole: userRole,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(checklistsRef, newChecklist);
    return {
      id: docRef.id,
      ...newChecklist,
    };
  } catch (error) {
    toast.error('Error creating audit checklist');
    throw error;
  }
};

/**
 * Update audit checklist
 */
export const updateAuditChecklist = async (id, checklistData) => {
  try {
    const checklistRef = doc(firestore, AUDIT_CHECKLISTS_COLLECTION, id);
    const updateData = {
      ...checklistData,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(checklistRef, updateData);
    return { id, ...updateData };
  } catch (error) {
    toast.error('Error updating audit checklist');
    throw error;
  }
};

/**
 * Delete audit checklist
 */
export const deleteAuditChecklist = async id => {
  try {
    const checklistRef = doc(firestore, AUDIT_CHECKLISTS_COLLECTION, id);
    await deleteDoc(checklistRef);
    return true;
  } catch (error) {
    toast.error('Error deleting audit checklist');
    throw error;
  }
};

/**
 * Export submissions to CSV
 */
export const exportSubmissionsToCSV = submissions => {
  if (!submissions || submissions.length === 0) return '';

  const headers = ['ID', 'Category', 'Status', 'Title', 'Client Email', 'Created At', 'Updated At'];

  const rows = submissions.map(sub => {
    const createdAt = sub.createdAt?.toDate
      ? sub.createdAt.toDate().toISOString()
      : sub.createdAt || '';
    const updatedAt = sub.updatedAt?.toDate
      ? sub.updatedAt.toDate().toISOString()
      : sub.updatedAt || '';

    return [
      sub.id || '',
      sub.category || '',
      sub.status || '',
      sub.title || '',
      sub.userEmail || '',
      createdAt,
      updatedAt,
    ];
  });

  const csvContent = [headers, ...rows]
    .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  return csvContent;
};

/**
 * Export submissions to JSON
 */
export const exportSubmissionsToJSON = submissions => {
  return JSON.stringify(
    submissions.map(sub => ({
      ...sub,
      createdAt: sub.createdAt?.toDate ? sub.createdAt.toDate().toISOString() : sub.createdAt,
      updatedAt: sub.updatedAt?.toDate ? sub.updatedAt.toDate().toISOString() : sub.updatedAt,
    })),
    null,
    2
  );
};

/**
 * Create audit for a submission
 */
export const createSubmissionAudit = async (submissionId, auditData) => {
  try {
    const auditsRef = collection(firestore, AUDITS_COLLECTION);
    const newAudit = {
      submissionId,
      ...auditData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(auditsRef, newAudit);

    // Update submission status if provided
    if (auditData.status) {
      await updateSubmission(submissionId, { status: auditData.status });
    }

    return {
      id: docRef.id,
      ...newAudit,
    };
  } catch (error) {
    toast.error('Error creating audit');
    throw error;
  }
};

/**
 * Get audits for a submission
 * Uses client-side sorting to avoid Firestore index requirement
 */
export const getSubmissionAudits = async submissionId => {
  try {
    const auditsRef = collection(firestore, AUDITS_COLLECTION);
    // Only filter by submissionId to avoid index requirement
    const q = query(auditsRef, where('submissionId', '==', submissionId));

    const querySnapshot = await getDocs(q);
    const audits = [];

    querySnapshot.forEach(doc => {
      const data = doc.data();
      audits.push({
        id: doc.id,
        ...data,
      });
    });

    // Sort client-side by createdAt (descending)
    audits.sort((a, b) => {
      const aDate = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      const bDate = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
      return bDate - aDate; // Descending order
    });

    return audits;
  } catch (error) {
    toast.error('Error fetching audits');
    throw error;
  }
};
